import * as React from 'react';
import { FC } from 'react';
import { Container, NavLink } from 'reactstrap';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { TK } from '../../store/translations/translationKeys';
import { getLanguage, useTranslations } from '../../store/translations/hooks';
import AppNameIcon from '../../shared/icons/AppNameIcon';
import AppIcon from '../../shared/icons/AppIcon';
import { Flag } from '../../components/Flag';
import { paths } from '../../app/constants';
import { clearAuthContext } from '../../shared/authContext';
import AppButton from '../../components/AppButton';

const Title = styled.h2`
    text-align: center;
    margin-bottom: var(--spacer-M);
    font-size: 2rem;
    font-weight: 600;
    color: var(--text-body);
`;

const Subtitle = styled.p`
    text-align: center;
    margin-bottom: var(--spacer-M);
    font-size: 1rem;
    color: var(--text-muted);
    line-height: 1.5;
`;

const InfoBox = styled.div`
    background-color: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--info-color);
    border-radius: var(--spacer-XS);
    padding: var(--spacer-M);
    margin-bottom: var(--spacer-M);
`;

const InfoText = styled.p`
    margin: 0;
    color: var(--text-body);
    font-size: 0.95rem;
    line-height: 1.5;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-M);
`;

const LanguageFlagWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: var(--spacer-XS);
    margin-top: var(--spacer-S);
`;

const CloseButton = styled.button`
    position: absolute;
    top: var(--spacer-XS);
    right: var(--spacer-XS);
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;
    z-index: 10;

    &:hover {
        color: var(--text-body);
    }
`;

const BackToLoginLink = styled.button`
    background: none;
    border: none;
    color: var(--appBlue);
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0;
    text-align: center;
    text-decoration: none;
    transition: opacity 0.2s;
    margin-top: var(--spacer-S);
    width: 100%;

    &:hover {
        opacity: 0.8;
        text-decoration: underline;
    }
`;

const IconWrapper = styled.div`
    text-align: center;
    font-size: 4rem;
    margin-bottom: var(--spacer-M);
    color: var(--text-muted);
`;

export interface NoClinicsYetProps {
    onCreateBusiness?: () => void;
}

const NoClinicsYet: FC<NoClinicsYetProps> = ({ onCreateBusiness }) => {
    const t = useTranslations();
    const history = useHistory();
    const supportedLanguages = ['pt', 'en', 'fr'] as const;
    const currentLanguage = getLanguage();

    const handleBackToLogin = () => {
        clearAuthContext();
        history.push(paths.login());
    };

    const handleAskForAccess = () => {
        // This could open a modal with instructions or redirect to a contact page
        window.open('https://appointmint.pt/contact', '_blank');
    };

    const handleCreateBusiness = () => {
        if (onCreateBusiness) {
            onCreateBusiness();
        } else {
            // Clear client context and redirect to professional login/onboarding
            clearAuthContext();
            history.push(paths.loginProfessional());
        }
    };

    return (
        <div className="loginPage">
            <Container className="centerContainer">
                <a href="https://appointmint.pt/"><AppNameIcon className="appNameIcon"/></a>
                <AppIcon className="appIcon appIcon1" />
                <AppIcon className="appIcon appIcon2" />

                <div className="loginForm" style={{position: 'relative'}}>
                    <CloseButton aria-label={t(TK.close)} onClick={() => window.location.href = 'https://appointmint.pt/'}>
                        ✕
                    </CloseButton>

                    <IconWrapper>🏥</IconWrapper>

                    <Title>{t(TK.noAssociatedClinics)}</Title>

                    <Subtitle>{t(TK.youHaveNoAssociatedClinics)}</Subtitle>

                    <InfoBox>
                        <InfoText>{t(TK.noClinicsExplanation)}</InfoText>
                    </InfoBox>

                    <ButtonsContainer>
                        <AppButton
                            color="primary"
                            onClick={handleAskForAccess}
                            block
                        >
                            {t(TK.askClinicForAccess)}
                        </AppButton>

                        <AppButton
                            color="secondary"
                            onClick={handleCreateBusiness}
                            block
                        >
                            {t(TK.createBusiness)}
                        </AppButton>
                    </ButtonsContainer>

                    <BackToLoginLink onClick={handleBackToLogin}>
                        {t(TK.login)}
                    </BackToLoginLink>

                    <LanguageFlagWrapper>
                        {supportedLanguages
                            .filter(lang => lang !== currentLanguage)
                            .map(lang => (
                                <NavLink key={lang} href={`/${lang}`}>
                                    <Flag flag={lang} />
                                </NavLink>
                            ))}
                    </LanguageFlagWrapper>
                </div>
            </Container>
        </div>
    );
};

export default NoClinicsYet;
